import { FileFromZip, FileManagerResult } from '@paragon/component-processor';
import { JobPriority, JobStatus, JobType, Region, User } from '@paragon/contract-library';
import { sealingService } from '@paragon/data-connections';
import { getClientUrls } from '@paragon/login-tools';
import { enqueueError, enqueueInfo } from '@paragon/notification-tools';

import {
  pAssignToUser as pAssignJobToUser,
  pCreateJobFromZip,
  pEditJob,
  pFetch as pFetchJob,
  pOpen as pOpenJob,
  pSetStatus as pSetJobStatus,
  pShareWithOrganizations as pShareJobWithOrganizations,
} from '../data/jobData';
import {
  finishAssigningUsersToJobs,
  finishInsertingJob,
  finishSettingStatus,
  finishSharingJobsWithOrganizations,
  finishUpdatingJob,
  JobProperties,
  JobWithUsers,
  startCreatingJobWithProperties,
  startEditingJobWithProperties,
} from '../store/jobsSlice';
import { setIsInsertingJob, setIsUpdatingJob } from '../store/statusesSlice';
import { AsyncThunkActionReturn, AsyncThunkActionVoid, ThunkActionVoid } from './thunkActionTypes';

function getInitialJobProperties(user: User | null): JobProperties {
  return {
    selectedRegions: [],
    selectedOrganizations: null, // Null for no user input, otherwise an array of organizations to share with (empty for unsharing)
    selectedJobPriority: 'Normal',
    billingLocation: user ? user.location : null,
  };
}

export const startCreatingJob = (): ThunkActionVoid => (dispatch, getState) => {
  const user = getState().logInData.loggedInUser!;
  const jobProperties = {
    ...getInitialJobProperties(getState().logInData.loggedInUser),
    contactName: `${user.firstName} ${user.lastName}`,
    contactPhone: user.phone,
    contactEmail: user.email,
  };
  dispatch(startCreatingJobWithProperties(jobProperties));
};

function getJobPropertiesForEditingJob(job: JobWithUsers, user: User, regions: readonly Region[]) {
  return {
    ...getInitialJobProperties(user),
    ...job,
    selectedRegions: regions.filter((region) => job.regions.includes(region.name)),
    comments: job.comments ?? '',
    selectedOrganizations: job.sharedWithOrganizations.filter((organization) => organization !== user!.organization),
    selectedJobType: job.jobType,
    selectedJobPriority: job.jobPriority,
  };
}

export const startEditingJob =
  (job: JobWithUsers): ThunkActionVoid =>
  (dispatch, getState) => {
    const user = getState().logInData.loggedInUser!;
    const { regions } = getState().regions;
    const jobProperties = getJobPropertiesForEditingJob(job, user, regions);
    dispatch(startEditingJobWithProperties({ jobBeingEditedId: job.id, properties: jobProperties }));
  };

export type RequiredAndNonNullable<T> = {
  [P in keyof T]-?: NonNullable<T[P]>;
};

export interface JobDataMessageWithoutId extends RequiredAndNonNullable<JobProperties> {
  readonly regions: readonly string[];
  readonly selectedZip: string;
  readonly fileManagerData: FileManagerResult;
  readonly jobType: JobType;
  readonly jobPriority: JobPriority;
  readonly sharedWithOrganizations: readonly string[];
  readonly convertToParagonTdds: boolean;
  readonly convertVerticalsUnderFifteenAndAHalfInchesToSpfNumberTwo: boolean;
}

export interface JobDataMessageWithId extends JobDataMessageWithoutId {
  readonly id: number;
}

export const insertJob =
  (jobValues: JobDataMessageWithoutId): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const currentUser = getState().logInData.loggedInUser!;

    const { selectedZip } = jobValues;
    const parameters = {
      ...jobValues,
      creator: currentUser,
      selectedZip: null,
    };

    dispatch(setIsInsertingJob(true));

    try {
      const insertedJob = await pCreateJobFromZip(
        parameters,
        selectedZip!.split(',')[1],
        jobValues.convertToParagonTdds,
      );

      const insertedJobWithUsers = {
        ...insertedJob,
        creator: currentUser,
        location: currentUser.location.length === 0 ? 'No Location' : currentUser.location,
        assignedTo: [],
        openedBy: [],
        numberOfDesigns: Object.keys(jobValues.fileManagerData.componentDesigns).length,
        numberOfMetaPdfs: Object.keys(jobValues.fileManagerData.metaPdfs).length,
        numberOfMiscFiles: Object.keys(jobValues.fileManagerData.miscellaneousFiles).length,
      };
      dispatch(finishInsertingJob(insertedJobWithUsers));
    } catch (error) {
      dispatch(enqueueError('Error creating job'));
      console.log(error);
    } finally {
      dispatch(setIsInsertingJob(false));
    }
  };

export const updateJob =
  (jobValues: JobDataMessageWithId): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    dispatch(setIsUpdatingJob(true));

    try {
      await pEditJob(jobValues);

      const currentUser = getState().logInData.loggedInUser;

      const job = getState().jobs.jobs.find((job) => job.id === jobValues.id);

      // update here
      if (!job || !currentUser) {
        // If no job or user found, return (for example, the edit was triggered by the user logging out)
        dispatch(setIsUpdatingJob(false));
        return;
      }

      // User cannot unshare with their own organization
      // So if the job being updated was shared with their own organization, continue to display that
      const sharedWithOrganizations = job.sharedWithOrganizations.includes(currentUser.organization)
        ? [...jobValues.sharedWithOrganizations, currentUser.organization]
        : jobValues.sharedWithOrganizations;
      const updatedJob = {
        ...job,
        ...jobValues,
        sharedWithOrganizations,
      };
      const { regions } = getState().regions;
      const jobProperties = getJobPropertiesForEditingJob(updatedJob, currentUser, regions);
      dispatch(finishUpdatingJob({ updatedJob, jobProperties }));
    } finally {
      dispatch(setIsUpdatingJob(false));
    }
  };

export const attemptToOpenJob =
  (jobId: number): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const localJob = getState().jobs.jobs.find((job) => job.id === jobId)!;
    const { markupUrl } = getClientUrls(getState());

    if (localJob.hasBeenPreProcessed) {
      pOpenJob(markupUrl, jobId);
    } else {
      // check again -- preprocessing might have finished since the jobs list was loaded
      const jobFromServer = await pFetchJob(jobId);
      if (jobFromServer.hasBeenPreProcessed) {
        pOpenJob(markupUrl, jobId);
      } else {
        dispatch(enqueueInfo('Preprocessing is not yet complete.'));
      }
    }
  };

export const shareJobsWithOrganizations =
  (message: {
    readonly jobIds: readonly number[];
    readonly organizationNames: readonly string[];
  }): AsyncThunkActionVoid =>
  async (dispatch) => {
    const { jobIds, organizationNames } = message;

    dispatch(setIsUpdatingJob(true));

    try {
      await Promise.all(jobIds.map((jobId) => pShareJobWithOrganizations(jobId, organizationNames)));
      dispatch(finishSharingJobsWithOrganizations({ jobIds, organizationNames }));
    } catch (err) {
      console.error(err);
      dispatch(setIsUpdatingJob(false));
    }
  };

export const assignUsersToJobs =
  (message: { readonly jobIds: readonly number[]; readonly userEmails: readonly string[] }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { jobIds, userEmails } = message;

    dispatch(setIsUpdatingJob(true));

    try {
      await Promise.all(
        jobIds.map((jobId) => {
          const job = getState().jobs.jobs.find((job) => job.id === jobId)!;
          const emails = job.assignedToEmails
            .filter((email) => !getState().organizations.usersInOrganization!.some((user) => user.email === email))
            .concat(userEmails);
          return pAssignJobToUser(jobId, emails);
        }),
      );
      const users = getState().organizations.usersInOrganization!.filter((user) => userEmails.includes(user.email));
      dispatch(finishAssigningUsersToJobs({ jobIds, users }));
    } catch (err) {
      dispatch(setIsUpdatingJob(false));
      console.error(err);
    }
  };

export const setStatus =
  (message: { readonly jobIds: readonly number[]; readonly status: JobStatus }): AsyncThunkActionVoid =>
  async (dispatch) => {
    const { jobIds, status } = message;

    dispatch(setIsUpdatingJob(true));

    try {
      await Promise.all(jobIds.map((jobId) => pSetJobStatus(jobId, status)));
      dispatch(finishSettingStatus({ jobIds, status }));
    } catch (err) {
      dispatch(setIsUpdatingJob(false));
      console.error(err);
    }
  };

export function validatePdfs(filesFromZip: readonly FileFromZip[]): AsyncThunkActionReturn<boolean> {
  return async function validatePdfsInJobZipThunkAction(dispatch) {
    const corruptFileNames: string[] = [];

    const pdfFilesFromZip = filesFromZip.filter((file) => file.filetype?.toLowerCase() === 'pdf');
    for (const pdfFileFromZip of pdfFilesFromZip) {
      try {
        await pdfFileFromZip.getContents();
      } catch (error) {
        corruptFileNames.push(`${pdfFileFromZip.filename}.${pdfFileFromZip.filetype}`);
      }
    }

    try {
      const pdfsToValidate = pdfFilesFromZip.map((pdfFileFromZip) => ({
        name: `${pdfFileFromZip.filename}.${pdfFileFromZip.filetype}`,
        base64Pdf: pdfFileFromZip.contents!,
      }));
      const { corruptPdfFileNames } = await sealingService.jobs.validatePdfs(pdfsToValidate);
      corruptFileNames.push(...corruptPdfFileNames);
    } catch (error) {
      console.error(error);
      dispatch(enqueueInfo('Could not perform PDF validation'));
      return true; // Let the user proceed
    }

    if (corruptFileNames.length > 0)
      dispatch(enqueueError(`Corrupt files: ${corruptFileNames.join(', ')}. Try regenerating the files.`));

    return corruptFileNames.length === 0;
  };
}
